/**
 * 認証ガード
 *
 * 認証が必要なルートを保護するコンポーネント
 * LaravelでいうAuth middlewareに相当
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../types';

interface AuthGuardProps {
  /** 子コンポーネント */
  children: React.ReactNode;
  /** 必要なロール（指定しない場合は認証済みであれば可） */
  requiredRoles?: UserRole[];
}

/**
 * ローディングスピナー
 */
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <svg
          className="animate-spin h-10 w-10 text-blue-600 mx-auto"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p className="mt-4 text-gray-600">読み込み中...</p>
      </div>
    </div>
  );
}

/**
 * アクセス拒否ページ
 */
function AccessDenied() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <div className="text-red-500 text-6xl mb-4">403</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          アクセス権限がありません
        </h1>
        <p className="text-gray-600 mb-4">
          このページにアクセスする権限がありません。
        </p>
        <a
          href="/home"
          className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          ホームに戻る
        </a>
      </div>
    </div>
  );
}

/**
 * 認証ガードコンポーネント
 *
 * 使用例:
 * <AuthGuard>
 *   <ProtectedPage />
 * </AuthGuard>
 *
 * ロールベースの制御:
 * <AuthGuard requiredRoles={['SYSTEM_ADMIN']}>
 *   <AdminPage />
 * </AuthGuard>
 */
export default function AuthGuard({ children, requiredRoles }: AuthGuardProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // 初期化中はローディング表示
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // 未認証の場合はログインページにリダイレクト
  if (!isAuthenticated) {
    // 現在のURLをstateに保存して、ログイン後にリダイレクトできるようにする
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ロールベースのアクセス制御
  if (requiredRoles && requiredRoles.length > 0 && user) {
    if (!requiredRoles.includes(user.role)) {
      return <AccessDenied />;
    }
  }

  // 認証済み＆権限ありの場合は子コンポーネントを表示
  return <>{children}</>;
}

/**
 * システム管理者専用ガード
 */
export function SystemAdminGuard({ children }: { children: React.ReactNode }) {
  return (
    <AuthGuard requiredRoles={['SYSTEM_ADMIN']}>
      {children}
    </AuthGuard>
  );
}

/**
 * 施設管理者以上専用ガード
 */
export function FacilityAdminGuard({ children }: { children: React.ReactNode }) {
  return (
    <AuthGuard requiredRoles={['SYSTEM_ADMIN', 'FACILITY_ADMIN']}>
      {children}
    </AuthGuard>
  );
}

/**
 * 検査者以上専用ガード（実質的には全認証ユーザー）
 */
export function ExaminerGuard({ children }: { children: React.ReactNode }) {
  return (
    <AuthGuard requiredRoles={['SYSTEM_ADMIN', 'FACILITY_ADMIN', 'EXAMINER']}>
      {children}
    </AuthGuard>
  );
}

/**
 * 施設所属ユーザー専用ガード（SYSTEM_ADMINを除外）
 *
 * SYSTEM_ADMINは施設に属さないため、患者管理や検査関連の機能にはアクセスできない
 */
export function FacilityMemberGuard({ children }: { children: React.ReactNode }) {
  return (
    <AuthGuard requiredRoles={['FACILITY_ADMIN', 'EXAMINER']}>
      {children}
    </AuthGuard>
  );
}
